import { getSnapshot } from 'mobx-state-tree';
import { cloneDeep, isEqual, unset } from 'lodash-es';
import { IPortal, Schema } from '@mcn-platform/models';
import { normalizeForCompare, MATCH_EMPTY_FIELD_DEFAULTS } from '../../constants/systemPromptDefaults';

export const prepareDirtyPortalAIConfig = (
  portal: IPortal,
  source: { config: any; snapshot: any },
  changed: Map<string, any>,
) => {
  if (!source) throw Error('Reload this portal before saving its AI configuration.');
  const snapshot = cloneDeep(getSnapshot(portal)) as any;
  if (source.snapshot.updatedAt !== snapshot.updatedAt) throw Error('This portal changed. Reload before saving its AI configuration.');
  const values: any = { ...portal };
  const edits = new Map(changed);
  const fields = [...edits.keys()];
  fields.forEach((field) => {
    if (field === 'clientAIConfig' || fields.some((other) => other !== field && other.startsWith(`${field}.`))) {
      throw Error(`Unsupported or overlapping AI configuration edit: ${field}`);
    }
  });
  const properties = Schema.getModel('portal').properties;
  const config = source.config == null ? {} : cloneDeep(source.config);
  if (typeof config !== 'object' || Array.isArray(config)) throw Error('AI configuration must be a JSON object.');
  const topLevel = new Set<string>();
  let configChanged = false;
  edits.forEach((raw, field) => {
    const value = Schema.normalizeFormValue(raw);
    if (value === undefined) throw Error(`Cannot save an undefined value for ${field}.`);
    if (!field.startsWith('clientAIConfig.')) {
      if (!properties[field] || properties[field].readOnly) return;
      snapshot[field] = value;
      values[field] = value;
      topLevel.add(field);
      return;
    }
    const path = field.slice('clientAIConfig.'.length).split('.');
    let node = config;
    path.slice(0, -1).forEach((key) => {
      if (Array.isArray(node) && (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= node.length)) {
        throw Error(`Invalid AI configuration index: ${field}`);
      }
      if (!Object.prototype.hasOwnProperty.call(node, key)) {
        Object.defineProperty(node, key, { value: {}, enumerable: true, writable: true, configurable: true });
      }
      node = node[key];
      if (!node || typeof node !== 'object') throw Error(`Invalid AI configuration path: ${field}`);
    });
    const key = path[path.length - 1];
    if (Array.isArray(node) && (!/^(0|[1-9]\d*)$/.test(key) || Number(key) >= node.length)) {
      throw Error(`Invalid AI configuration index: ${field}`);
    }
    const def = path.length === 1 ? MATCH_EMPTY_FIELD_DEFAULTS[key] : undefined;
    const authored = def !== undefined && typeof value === 'string'
      && normalizeForCompare(value) === normalizeForCompare(def) ? '' : value;
    Object.defineProperty(node, key, { value: cloneDeep(authored), enumerable: true, writable: true, configurable: true });
    configChanged = true;
  });
  const serialized = Schema.getValuesForUpdate('portal', values, false, false);
  const input: Record<string, any> = {};
  topLevel.forEach((key) => { input[properties[key].fieldName || key] = serialized[properties[key].fieldName || key]; });
  if (configChanged) input.clientAIConfig = JSON.stringify(config);
  return { input, config: configChanged ? config : source.config, snapshot, changes: edits };
};

export const preparePortalDesignerSave = (portal: IPortal) => {
  const source = Schema.getPortalAIConfigSource(portal as any);
  const changed = Schema.getPortalAIConfigEdits(portal as any);
  const prepared = prepareDirtyPortalAIConfig(portal, source, changed);
  const previousConfig = cloneDeep(source.snapshot.clientAIConfig);
  const currentConfig = cloneDeep(prepared.snapshot.clientAIConfig);
  changed.forEach((_, field) => {
    if (field.startsWith('clientAIConfig.')) {
      const path = field.slice('clientAIConfig.'.length);
      unset(previousConfig, path);
      unset(currentConfig, path);
    }
  });
  if (!isEqual(previousConfig, currentConfig)) {
    throw Error('AI configuration changed without captured edits. Reload before saving in Designer.');
  }
  const input = { ...prepared.input };
  const serialized = Schema.getValuesForUpdate('portal', portal, false, false);
  Object.entries(Schema.getModel('portal').properties).forEach(([field, definition]: [string, any]) => {
    if (field === 'clientAIConfig' || definition.readOnly) return;
    if (!isEqual(source.snapshot[field], prepared.snapshot[field])) {
      const key = definition.fieldName || field;
      if (!Object.prototype.hasOwnProperty.call(input, key)) input[key] = serialized[key];
    }
  });
  return { ...prepared, input };
};
